import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import ContactSelector from '../../components/contacts/ContactSelector';
import { ExtractedLink } from './emailBuilderLinkUtils';
import { replacePlaceholders } from '../../utils/helper';
import { IContact } from 'types/contacts/contacts.types';
import {
  checkLinksRequest,
  clearCheckLinksResult,
} from '../../store/emailTemplates/emailTemplates.action';
import {
  selectCheckLinksLoading,
  selectCheckLinksResult,
  selectCheckLinksError,
} from '../../store/emailTemplates/emailTemplates.selector';
import { Dot } from 'lucide-react';

/** http/https links are considered trackable; mailto, tel, ftp are untracked. */
function getTrackingLabel(href: string): 'Tracked' | 'Untracked' {
  const lower = href.trim().toLowerCase();
  if (lower.startsWith('http:') || lower.startsWith('https:')) {
    return 'Tracked';
  }
  return 'Untracked';
}

interface ReviewLinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  links: ExtractedLink[];
}

const ReviewLinksModal: React.FC<ReviewLinksModalProps> = ({
  isOpen,
  onClose,
  links,
}) => {
  const dispatch = useDispatch();
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);

  const checkLinksLoading = useSelector(selectCheckLinksLoading);
  const checkLinksResult = useSelector(selectCheckLinksResult);
  const checkLinksError = useSelector(selectCheckLinksError);

  const handleClose = () => {
    setSelectedContactId('');
    setSelectedContact(null);
    dispatch(clearCheckLinksResult());
    onClose();
  };

  const sortedLinks = [...links].sort((a, b) => {
    const aTracked = getTrackingLabel(a.href) === 'Tracked';
    const bTracked = getTrackingLabel(b.href) === 'Tracked';
    if (aTracked === bTracked) return 0;
    return aTracked ? -1 : 1;
  });

  const contactData = selectedContact
    ? ({ ...selectedContact, ...(selectedContact.attributes ?? {}) } as Record<
        string,
        unknown
      >)
    : undefined;

  const getResolvedHref = (href: string) =>
    contactData ? replacePlaceholders(href, { contact: contactData }) : href;

  const resultByUrl = React.useMemo(() => {
    if (!checkLinksResult) return null;
    const map: Record<string, (typeof checkLinksResult)[number]> = {};
    checkLinksResult.forEach((r) => {
      map[r.url] = r;
    });
    return map;
  }, [checkLinksResult]);

  const handleCheckLinks = () => {
    const resolvedUrls = sortedLinks
      .map((link) => getResolvedHref(link.href))
      .filter((url) => url.trim().length > 0);
    const unique = Array.from(new Set(resolvedUrls));
    if (unique.length > 0) {
      dispatch(checkLinksRequest(unique));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Review Links"
      size="large"
      className="max-w-3xl"
    >
      <div className="flex flex-col min-h-0">
        {/* Contact selector */}
        <div className="pb-4 border-b border-border mb-2">
          <ContactSelector
            value={selectedContactId}
            onChange={(contact) => {
              setSelectedContactId(contact._id ?? '');
              setSelectedContact(contact);
            }}
            label="Select contact"
            required
            fetchWhenVisible
            isVisible={isOpen}
            autoSelectFirst
          />
        </div>

        {/* Table header */}
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-1 pb-2 border-b border-border text-sm font-medium text-muted-foreground">
          <span>Link</span>
          <span className="flex items-center gap-1.5">Status</span>
          <span className="w-24 text-center">Tracking</span>
        </div>

        {/* Table body */}
        <div className="flex-1 overflow-y-auto max-h-[50vh] py-2">
          {sortedLinks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No links found in the current template.
            </p>
          ) : (
            <div className="space-y-1">
              {sortedLinks.map((link, index) => {
                const resolvedHref = getResolvedHref(link.href);
                const result = resultByUrl?.[resolvedHref];
                const hasStatusCode =
                  result != null && typeof result.status_code === 'number';
                const statusDisplay =
                  result == null
                    ? '—'
                    : hasStatusCode
                      ? String(result.status_code)
                      : '';
                const statusOk =
                  hasStatusCode &&
                  result.status_code >= 200 &&
                  result.status_code < 400;
                const errorDisplay =
                  result?.error?.trim() ||
                  (result?.timeout ? 'Request timed out.' : null);
                return (
                  <div key={`${link.href}-${index}`} className="text-sm">
                    <div className="grid grid-cols-[1fr_auto_auto] gap-4 items-center py-2.5 px-2">
                      <code className="text-foreground break-all pr-2">
                        {resolvedHref}
                      </code>
                      <span
                        className={
                          result
                            ? statusOk
                              ? 'text-green-600 dark:text-green-400'
                              : hasStatusCode
                                ? 'text-destructive'
                                : 'text-muted-foreground'
                            : 'text-muted-foreground'
                        }
                      >
                        {statusDisplay}
                      </span>
                      <span className="w-24 flex justify-center">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${
                            getTrackingLabel(resolvedHref) === 'Tracked'
                              ? 'bg-muted text-muted-foreground'
                              : 'bg-muted/80 text-muted-foreground'
                          }`}
                        >
                          {getTrackingLabel(resolvedHref)}
                        </span>
                      </span>
                    </div>
                    {errorDisplay && (
                      <p className="flex items-center gap-2 text-sm text-destructive pl-2 pb-1.5">
                        <Dot className="w-4 h-4 text-destructive" />
                        {errorDisplay}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {checkLinksError && (
          <p className="text-sm text-destructive mt-2">{checkLinksError}</p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-4 mt-2 border-t border-border">
          <Button variant="outline" size="small" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="primary"
            size="small"
            disabled={sortedLinks.length === 0 || checkLinksLoading}
            onClick={handleCheckLinks}
          >
            {checkLinksLoading ? 'Checking…' : 'Check Links'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReviewLinksModal;
